import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { lower } from "../../lib/strings";

const ACTION_COLOR = {
  create: "text-acid",
  update: "text-white",
  skip: "text-white/40",
  conflict: "text-yellow-400",
  invalid: "text-red-400",
};

// Admin-only: paste a JSON array of shows, preview the create/update/skip/conflict
// breakdown, then commit. Commit re-runs validation server-side; only valid
// create + update + audit-only-skip rows hit the DB.
export default function AdminImport() {
  const [raw, setRaw] = useState("");
  const [results, setResults] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [committed, setCommitted] = useState(null);

  const onPreview = async () => {
    setBusy(true);
    setParseError(null);
    setResults(null);
    setSummary(null);
    setCommitted(null);
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      setParseError(`json parse: ${e.message}`);
      setBusy(false);
      return;
    }
    const items = Array.isArray(parsed) ? parsed : parsed?.items;
    if (!Array.isArray(items)) {
      setParseError("expected an array of show objects, or an object with `items: [...]`");
      setBusy(false);
      return;
    }
    try {
      const { data } = await api.post("/admin/import-shows/preview", { items });
      setResults(data.results);
      setSummary(data.summary);
    } catch (err) {
      toast(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onCommit = async () => {
    if (!results) return;
    setBusy(true);
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      setParseError(`json parse: ${e.message}`);
      setBusy(false);
      return;
    }
    const items = Array.isArray(parsed) ? parsed : parsed?.items;
    try {
      const { data } = await api.post("/admin/import-shows/commit", { items });
      setResults(data.results);
      setSummary(data.summary);
      setCommitted(data.applied);
      setConfirming(false);
      toast(`commit: +${data.applied.created} created · ${data.applied.updated} updated · ${data.applied.skipped_audit_refreshed} re-checked`);
    } catch (err) {
      toast(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="px-5 py-6" data-testid="admin-import-page">
      <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
        bulk import — paste json
      </h2>

      <textarea
        data-testid="import-json-input"
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        placeholder='[ { "title": "Hamlet", "company": "Soulpepper", "venue": "Young Centre", "startDate": "2026-05-10", "endDate": "2026-05-31", "dateLabel": "may 10 – may 31", "sourceUrl": "https://soulpepper.ca/...", "sourceName": "Soulpepper", "verificationStatus": "verified" } ]'
        rows={12}
        className="w-full bg-transparent border-2 border-white/30 px-3 py-3 font-mono text-xs placeholder:text-white/30 focus:border-acid outline-none resize-y"
      />

      {parseError && (
        <p
          data-testid="import-parse-error"
          className="mt-3 font-mono text-[11px] text-red-400"
        >
          {parseError}
        </p>
      )}

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <button
          data-testid="import-preview-btn"
          onClick={onPreview}
          disabled={busy || !raw.trim()}
          className="font-mono text-xs lowercase border-2 border-acid text-acid px-4 py-2.5 hover:bg-acid hover:text-black transition-colors disabled:opacity-40"
        >
          {busy ? "previewing..." : "preview"}
        </button>
        {results && summary && !confirming && !committed && (
          <button
            data-testid="import-commit-btn"
            onClick={() => setConfirming(true)}
            className="font-mono text-xs lowercase border-2 border-acid bg-acid text-black px-4 py-2.5 hover:bg-black hover:text-acid transition-colors"
          >
            commit ({(summary.counts.create || 0)} create · {(summary.counts.update || 0)} update)
          </button>
        )}
      </div>

      {confirming && (
        <div
          data-testid="import-confirm-bar"
          className="mt-4 border-2 border-acid bg-acid/5 p-3 font-mono text-xs"
        >
          <p className="text-acid">
            commit this import to the live database?
          </p>
          <p className="text-white/60 mt-1 text-[11px] leading-relaxed">
            invalid + conflict rows will be skipped. {(summary?.counts.create || 0)} new shows will be created.
            {(summary?.counts.update || 0)} existing shows will be updated.
            audit-only re-checks will refresh the lastCheckedAt timestamp.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <button
              data-testid="import-confirm-yes"
              onClick={onCommit}
              disabled={busy}
              className="font-mono text-[11px] uppercase tracking-wider bg-acid text-black px-3 py-1.5 border-2 border-acid hover:bg-black hover:text-acid transition-colors disabled:opacity-40"
            >
              {busy ? "committing..." : "commit"}
            </button>
            <button
              data-testid="import-confirm-no"
              onClick={() => setConfirming(false)}
              disabled={busy}
              className="font-mono text-[11px] uppercase tracking-wider border-2 border-white/30 text-white/60 px-3 py-1.5 hover:border-white hover:text-white transition-colors disabled:opacity-40"
            >
              cancel
            </button>
          </div>
        </div>
      )}

      {summary && (
        <div data-testid="import-summary" className="mt-6 border-2 border-white/15 p-4">
          <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-3">
            summary · total {summary.total}
          </h3>
          <ul className="grid grid-cols-2 sm:grid-cols-5 gap-2 font-mono text-[11px]">
            {["create", "update", "skip", "conflict", "invalid"].map((a) => (
              <li
                key={a}
                data-testid={`import-summary-${a}`}
                className={`border ${a === "create" ? "border-acid" : "border-white/20"} p-2 flex flex-col`}
              >
                <span className={`text-2xl tabular-nums ${ACTION_COLOR[a]}`}>
                  {summary.counts[a] ?? 0}
                </span>
                <span className="uppercase tracking-wider text-white/40">{a}</span>
              </li>
            ))}
          </ul>
          {committed && (
            <p
              data-testid="import-committed-banner"
              className="mt-3 font-mono text-[11px] text-acid"
            >
              committed · created: {committed.created} · updated: {committed.updated} · re-checked: {committed.skipped_audit_refreshed}
            </p>
          )}
        </div>
      )}

      {results && (
        <ul className="mt-6 space-y-2" data-testid="import-results">
          {results.map((r, i) => (
            <li
              key={`${r.action}-${i}`}
              data-testid={`import-row-${r.action}-${i}`}
              className="border border-white/10 px-3 py-2 font-mono text-[11px] flex items-baseline gap-3 flex-wrap"
            >
              <span className={`uppercase tracking-wider ${ACTION_COLOR[r.action]} shrink-0`}>
                {r.action}
              </span>
              <span className="text-white/80 truncate flex-1 min-w-0">
                {lower(r.title)}
              </span>
              {r.id && <span className="text-white/40 text-[10px]">{r.id}</span>}
              {r.errors && (
                <span className="text-red-400 text-[10px] basis-full">
                  {r.errors.join(" · ")}
                </span>
              )}
              {r.applied_fields && r.applied_fields.length > 0 && (
                <span className="text-acid text-[10px] basis-full">
                  → {r.applied_fields.join(", ")}
                </span>
              )}
              {r.conflict_fields && r.conflict_fields.length > 0 && (
                <span className="text-yellow-400 text-[10px] basis-full">
                  ⚠ keeping existing: {r.conflict_fields.join(", ")}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
